import {
  faCheck,
  faExternalLinkAlt,
  faHexagonExclamation,
  faCopy,
} from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Response } from '../queries/getQuizQuery';
import DownloadTeamReport from './DownloadTeamReport';
import React, { useState } from 'react';
import TeamApprovalModal, { AiTeamReportData } from './TeamApprovalModal';
import DownloadAllReportsModal from './DownloadAllReportsModal';
import { UPDATE_QUIZ } from '../mutations/updateQuizReportData';
import { useMutation } from '@apollo/client';

type Props = {
  readonly survey: Response['quiz'];
  readonly userRole: string | null;
  readonly reportLink: string;
  readonly actions?: React.ReactNode;
  readonly refetch: () => void;
};

export default function SurveyOverview({
                                         survey,
                                         userRole,
                                         reportLink,
                                         actions,
                                         refetch,
                                       }: Props) {
  const [updateQuizReportData, { loading: saving }] = useMutation(UPDATE_QUIZ);
  const [openTeamApprovalModal, setOpenTeamApprovalModal] = useState(false);
  const [openDownloadAllModal, setOpenDownloadAllModal] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);

  const individualCount = survey.responses.filter(
      (r) => r.respondentType === 'INDIVIDUAL',
  ).length;

  const leaderCount = survey.responses.filter(
      (r) => r.respondentType === 'LEADER',
  ).length;

  const totalCount = survey.responses.length;

  const handleCopy = async (text: string, type: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(type);
      setTimeout(() => setCopied(null), 2000);
    } catch (err) {
      console.error('Copy failed', err);
    }
  };

  return (
      <>
        {/* Survey details */}
        <div className="rounded-xl shadow-md p-6 bg-white space-y-4">
          <h2 className="text-lg font-semibold mb-4">Survey details</h2>

          <div className="flex items-center space-x-4">
            <label className="w-32 text-sm font-medium text-gray-700">
              Survey name
            </label>
            <span className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-900 bg-white">
            {survey.title}
          </span>
          </div>

          <div className="flex items-center space-x-4">
            <label className="w-32 text-sm font-medium text-gray-700">
              Client name
            </label>
            <span className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-900 bg-white">
            {survey.client.name}
          </span>
          </div>

          <div className="flex items-center space-x-4">
            <label className="w-32 text-sm font-medium text-gray-700">
              Team name
            </label>
            <span className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-900 bg-white">
            {survey.teamName}
          </span>
          </div>
        </div>

        {/* Survey status & stats */}
        <div className="rounded-xl shadow-md p-6 bg-gray-200 space-y-6 mt-6">
          <div className="grid grid-cols-4 gap-4 text-sm">
            <div className="font-semibold">Survey status</div>
            <div>
            <span
                className={
                  survey.closed
                      ? 'text-red-600 font-bold'
                      : 'text-green-600 font-bold'
                }
            >
              {survey.closed ? 'Closed' : 'Active'}
            </span>
            </div>
            <div></div>
            <div>{actions}</div>
          </div>

          {/* Grid with 4 columns */}
          <div className="grid grid-cols-4 gap-4 text-sm">
            <div className="font-semibold">Responses</div>
            <div>
              Individuals
              <div className="border border-gray-400 bg-white rounded px-2 py-1 text-center w-10 inline-block ml-1">
                {individualCount}
              </div>
            </div>
            <div>
              Leaders
              <div className="border border-gray-400 bg-white rounded px-2 py-1 text-center w-10 inline-block ml-1">
                {leaderCount}
              </div>
            </div>
            <div>
              <strong>Total</strong>
              <div className="border border-gray-400 bg-white rounded px-2 py-1 text-center w-10 font-bold inline-block ml-1">
                {totalCount}
              </div>
            </div>
          </div>

          {/* Links */}
          <div className="grid grid-cols-4 gap-4 text-sm items-start">
            <div className="font-semibold">Links</div>

            {/* Individual link */}
            <div className="flex items-center space-x-2">
              <a
                  className="inline-flex items-center space-x-1 text-blue-600 hover:text-blue-900"
                  target="_blank"
                  href={survey.individualLink}
                  rel="noopener noreferrer"
              >
                <span>Individual survey</span>
                <FontAwesomeIcon size="xs" icon={faExternalLinkAlt} />
              </a>
              <button
                  onClick={() => handleCopy(survey.individualLink, 'individual')}
                  className="text-gray-500 hover:text-gray-700"
              >
                <FontAwesomeIcon
                    size="sm"
                    icon={copied === 'individual' ? faCheck : faCopy}
                />
              </button>
            </div>

            {/* Leader link */}
            <div className="flex items-center space-x-2">
              <a
                  className="inline-flex items-center space-x-1 text-blue-600 hover:text-blue-900"
                  target="_blank"
                  href={survey.leaderLink}
                  rel="noopener noreferrer"
              >
                <span>Leaders survey</span>
                <FontAwesomeIcon size="xs" icon={faExternalLinkAlt} />
              </a>
              <button
                  onClick={() => handleCopy(survey.leaderLink, 'leader')}
                  className="text-gray-500 hover:text-gray-700"
              >
                <FontAwesomeIcon
                    size="sm"
                    icon={copied === 'leader' ? faCheck : faCopy}
                />
              </button>
            </div>
          </div>

          {userRole === 'Admin' && (
              <div className="grid grid-cols-4 gap-4 items-center">
                <div className="font-semibold">Documents</div>
                <div>
                  <div className="flex" style={{ paddingTop: '5px' }}>
                    <button onClick={() => setOpenTeamApprovalModal(true)}>
                      {survey.isTeamReportApproved ? (
                          <FontAwesomeIcon
                              icon={faCheck}
                              className="bg-green-500 text-white rounded text-xl mr-2"
                              style={{ height: '24px', width: '24px', padding: '5px' }}
                          />
                      ) : (
                          <FontAwesomeIcon
                              icon={faHexagonExclamation}
                              className="bg-yellow-500 text-white rounded text-xl mr-2"
                              style={{ height: '24px', width: '24px', padding: '5px' }}
                          />
                      )}
                    </button>
                    <DownloadTeamReport quiz={survey} />
                  </div>
                </div>
                <div>
                  <a
                      target="_blank"
                      className="bg-gray-600 text-white px-3 py-2 rounded shadow hover:bg-gray-700"
                      href={reportLink}
                  >
                    <span>Download survey data</span>
                  </a>
                </div>
                <div className="relative">
                  <button
                    onClick={() => setOpenDownloadAllModal(true)}
                    className="bg-gray-600 text-white px-3 py-1 rounded shadow hover:bg-gray-700"
                  >
                    Download All Reports
                  </button>
                  {survey.zipReportUrl && survey.zipReportGeneratedAt && (
                    <div className="absolute left-0 mt-1">
                      <a
                        href={survey.zipReportUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-blue-600 hover:text-blue-900 whitespace-nowrap"
                      >
                        Last ZIP: {new Date(survey.zipReportGeneratedAt).toLocaleDateString()}
                      </a>
                    </div>
                  )}
                </div>
              </div>
          )}
        </div>
        {openDownloadAllModal && (
          <DownloadAllReportsModal
            survey={survey}
            onClose={() => setOpenDownloadAllModal(false)}
            refetch={refetch}
          />
        )}
        <TeamApprovalModal
            open={openTeamApprovalModal}
            onClose={() => setOpenTeamApprovalModal(false)}
            onSave={async (data) => {
              try {
                await updateQuizReportData({
                  variables: {
                    id: survey.id,
                    isTeamReportApproved: data.isReportApproved,
                    aiReportData: data.aiReportData,
                  },
                });
                refetch();
              } catch (err) {
                console.error('Error updating team report data', err);
              }
            }}
            initialData={{
              reportUrl: survey.reportUrl ?? '',
              isReportApproved: survey.isTeamReportApproved ?? false,
              aiReportData: survey.aiReportData
                  ? (survey.aiReportData as AiTeamReportData)
                  : { foreignTraits: [], dominantTraits: [] },
            }}
            isLoading={saving}
        />
      </>
  );
}
